<template>
  <div class="dynamic-document-form">
    <div v-if="!ready" class="ddf-loading">Cargando documento…</div>
    <DocumentOnline
      v-else-if="isPaged"
      ref="doc"
      :document="fakeDocument"
      :field_values="fakeDocument.context"
      :leave="false"
      :localMode="true"
    />
    <FUReev
      v-else
      ref="doc"
      :document="fakeDocument"
      :field_values="fakeDocument.context"
      :leave="false"
      :localMode="true"
    />
  </div>
</template>

<script>
// Embeds the REAL online-document fill components (DocumentOnline for paged
// templates, FUReev for the rest) so the chatbot reuses the exact UI a school
// user gets — but studentless: no Document is persisted and no websocket is
// required. We feed a fake `document` whose `context` is the live
// field_values object (mutated in place as the user answers).
//
// `:localMode="true"` makes the shared documentSocket mixin skip ALL
// socket/ping/persistence — updateInput/blockInput/joinDocument etc. become
// no-ops and inputs are never blocked, so answers live only in the local
// field_values dict and nothing is written to the DB. They are collected with
// getFieldValues() and resolved into a render context at download time.
//
// Separately, the components still make a few HTTP calls in `created` keyed by
// document id (getFureevContext / checkAutoUpdateDocument / getPageGraphData)
// plus the paged getPageInfo. While this wrapper is mounted we monkey-patch
// those service singletons so they resolve harmlessly, or — for getPageInfo —
// hit the studentless page-info endpoint instead of 404ing on the fake id.
// Same singleton-override trick as DynamicTestForm.vue; restored in
// beforeDestroy.
import DocumentOnline from "@/components/redpie/miscellaneous/DocumentOnline";
import FUReev from "@/components/redpie/miscellaneous/FUReev";
import documentOnlineService from "@/services/documentOnlineService";
import redpieService from "@/services/redpieService";
import individualDocumentService from "@/services/individualDocumentService";

const FAKE_DOCUMENT_ID = "individual-doc";

export default {
  name: "DynamicDocumentForm",
  components: { DocumentOnline, FUReev },
  props: {
    // { template_id, document_template, document_type } from getBlankTemplate.
    templateData: { type: Object, required: true },
    school: { type: [Number, String], required: false, default: null },
    grade: { type: [Number, String], required: false, default: null },
    diagnostic: { type: [Number, String], required: false, default: null },
    // Whether to KEEP the document's first "Identificación" page (the user
    // opted to fill it in the chat). When false, that page is dropped.
    fillFirstPage: { type: Boolean, required: false, default: false },
    // Auto-fill values keyed by document variable (e.g. { H, M, AM }). Seeded
    // into the field_values so they appear pre-filled (and render even when the
    // field's page was dropped — buildRenderContext passes plain values through).
    defaults: { type: Object, required: false, default: () => ({}) },
    // { role, data } — the logged-in user offered as the option of the
    // USER_SELECTOR whose role matches `role`. null = no user-select injection.
    userSelect: { type: Object, required: false, default: null },
  },
  data() {
    return {
      ready: false,
      currentPage: 0,
      fakeDocument: {
        id: FAKE_DOCUMENT_ID,
        context: {},
        state: 1,
        document_template: this.templateData.document_template,
      },
    };
  },
  computed: {
    isPaged() {
      const tpl = this.templateData.document_template || {};
      return Array.isArray(tpl.pages) && tpl.pages.length > 0;
    },
    totalPages() {
      const tpl = this.templateData.document_template || {};
      return this.isPaged ? tpl.pages.length : 1;
    },
  },
  mounted() {
    // Keep currentPage in sync with the embedded DocumentOnline's pageIndex
    // (the b-tabs v-model) — covers both our nav buttons and any internal
    // change — and report page state up to DocumentFillStep.
    this.$watch(
      () => (this.$refs.doc ? this.$refs.doc.pageIndex : 0),
      (val) => {
        this.currentPage = val || 0;
        this.emitPageState();
      }
    );
    this.$nextTick(() => this.emitPageState());
  },
  created() {
    // Neutralise / redirect the document-id-coupled HTTP service calls the
    // embedded components make (socket/persistence is handled by localMode).
    this._orig = {
      getFureevContext: redpieService.getFureevContext,
      checkAutoUpdateFureev: redpieService.checkAutoUpdateFureev,
      checkAutoUpdateDocument: redpieService.checkAutoUpdateDocument,
      manualSaveFureev: redpieService.manualSaveFureev,
      updateFureev: redpieService.updateFureev,
      updateDocumentValues: redpieService.updateDocumentValues,
      getPageInfo: documentOnlineService.getPageInfo,
      getPageGraphData: documentOnlineService.getPageGraphData,
    };
    // Return the SHAPE each consumer expects so their getters don't break:
    // getFureevContext -> actualContext (object, read via Object.keys);
    // checkAutoUpdate* -> retrievedContext (array, read via .includes).
    redpieService.getFureevContext = () => Promise.resolve({});
    redpieService.checkAutoUpdateFureev = () => Promise.resolve([]);
    redpieService.checkAutoUpdateDocument = () => Promise.resolve([]);
    redpieService.manualSaveFureev = () => Promise.resolve({ message: "" });
    redpieService.updateFureev = () => Promise.resolve({ message: "" });
    redpieService.updateDocumentValues = () => Promise.resolve({ message: "" });
    documentOnlineService.getPageGraphData = () => Promise.resolve({});
    // Paged templates lazy-load each page's rows by document id; route that to
    // the studentless endpoint using the picked school/grade/diagnostic, and
    // seed the local context for that page's structured fields.
    documentOnlineService.getPageInfo = (pageId) =>
      individualDocumentService
        .getPageInfo({
          page: pageId,
          template: this.templateData.template_id,
          school: this.school != null ? this.school : "",
          grade: this.grade != null ? this.grade : "",
          diagnostic: this.diagnostic != null ? this.diagnostic : "",
        })
        .then((data) => {
          if (data && Array.isArray(data.rows)) {
            data.rows = this.filterHiddenSections(data.rows);
          }
          this.applyUserSelect((data && data.rows) || []);
          this.initContext((data && data.rows) || []);
          return data;
        });

    // Local (in-memory) option editing. The modal's "add option" features
    // normally create DocumentTemplateFieldOption / special options / eje tags
    // in the DB. In the chatbot we keep them local: each service resolves with
    // a synthetic object (a generated id) that the component pushes into its
    // in-memory options array, so the user can add/remove options but they are
    // forgotten on reset/close (the template is re-fetched fresh each session).
    // No DB write.
    this._origDO = {
      addOption: documentOnlineService.addOption,
      addSpecialOption: documentOnlineService.addSpecialOption,
      saveSpecialOption: documentOnlineService.saveSpecialOption,
      deleteSpecialOption: documentOnlineService.deleteSpecialOption,
      deleteOption: documentOnlineService.deleteOption,
      addUserTestTag: documentOnlineService.addUserTestTag,
      addDimension: documentOnlineService.addDimension,
      deleteDimension: documentOnlineService.deleteDimension,
      addElementAchievement: documentOnlineService.addElementAchievement,
      clearElementAchievements: documentOnlineService.clearElementAchievements,
    };
    documentOnlineService.addOption = (p) =>
      Promise.resolve({
        id: this.localUid(),
        key: p.key,
        text: p.text,
        level: p.level != null ? p.level : null,
        is_global: false,
      });
    documentOnlineService.addSpecialOption = (p) =>
      Promise.resolve({ id: this.localUid(), key: p.key, data: p.data });
    documentOnlineService.addUserTestTag = (p) =>
      Promise.resolve({ id: this.localUid(), name: p.name, eje: p.eje });
    documentOnlineService.saveSpecialOption = () =>
      Promise.resolve({ message: "Guardado" });
    documentOnlineService.deleteOption = () =>
      Promise.resolve({ message: "Opción eliminada" });
    documentOnlineService.deleteSpecialOption = () =>
      Promise.resolve({ message: "Opción eliminada" });
    documentOnlineService.deleteDimension = () => Promise.resolve({});
    documentOnlineService.addElementAchievement = () => Promise.resolve({});
    documentOnlineService.clearElementAchievements = () => Promise.resolve({});
    // addDimension creates a whole new row server-side; build an equivalent
    // local row (a label + an empty CUSTOM_CHECKBOX) so the feature works
    // locally without a DB write.
    documentOnlineService.addDimension = (p) => {
      const fieldKey = "localdim_" + this.localUid();
      return Promise.resolve({
        field_key: fieldKey,
        row: {
          id: this.localUid(),
          order: 9999,
          school: p.school_id || null,
          fields: [
            {
              id: this.localUid(),
              key: null,
              kind: 0,
              is_title: false,
              text: p.name,
              name: p.name,
              order: 0,
            },
            {
              id: this.localUid(),
              key: fieldKey,
              kind: 7,
              name: p.name,
              text: "",
              order: 1,
              custom_checkbox_options: [],
              options: [],
            },
          ],
        },
      });
    };

    const tpl = this.templateData.document_template || {};
    // Hide the WHOLE first page when it is the "Identificación" page (student
    // identification + the professionals that brindarán apoyo) — UNLESS the
    // user chose to fill it in the chat (`fillFirstPage`). Dropping it from
    // `pages` removes it from the b-tabs, the page stepper and the render
    // context in one shot; the backend still renders those fields blank from the
    // full template. Replaces the per-row hiding of the IDENTIFICACIÓN sections.
    if (!this.fillFirstPage && Array.isArray(tpl.pages) && tpl.pages.length) {
      const firstName = (tpl.pages[0] && tpl.pages[0].name) || "";
      const norm = firstName
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
      if (norm.indexOf("identificacion") !== -1) {
        tpl.pages = tpl.pages.slice(1);
      }
    }

    // Non-paged (FUReev) templates carry their rows inline — drop hidden
    // sections, then seed those rows so OA / checkbox fields have their empty
    // shape before first render.
    if (!Array.isArray(tpl.pages) || !tpl.pages.length) {
      tpl.rows = this.filterHiddenSections(tpl.rows || []);
      this.applyUserSelect(tpl.rows);
      this.initContext(tpl.rows);
    }

    // Seed auto-fill defaults (grade / today's date / diagnosis) onto their
    // document variable keys, without clobbering anything already present.
    const ctx = this.fakeDocument.context;
    Object.keys(this.defaults || {}).forEach((k) => {
      if (this.defaults[k] != null && ctx[k] === undefined) {
        this.$set(ctx, k, this.defaults[k]);
      }
    });
    // FU de reevaluación starting state (FUReevContextEnum.DIAGNOSIS_VALUES).
    // The enum stores flags as "True"; the BOOL checkbox binds `true`.
    const initial = this.templateData.initial_values || {};
    Object.keys(initial).forEach((k) => {
      const v = initial[k];
      if (v !== "" && v != null && ctx[k] === undefined) {
        this.$set(ctx, k, v === "True" ? true : v);
      }
    });

    this.ready = true;
  },
  beforeDestroy() {
    if (this._orig) {
      redpieService.getFureevContext = this._orig.getFureevContext;
      redpieService.checkAutoUpdateFureev = this._orig.checkAutoUpdateFureev;
      redpieService.checkAutoUpdateDocument = this._orig.checkAutoUpdateDocument;
      redpieService.manualSaveFureev = this._orig.manualSaveFureev;
      redpieService.updateFureev = this._orig.updateFureev;
      redpieService.updateDocumentValues = this._orig.updateDocumentValues;
      documentOnlineService.getPageInfo = this._orig.getPageInfo;
      documentOnlineService.getPageGraphData = this._orig.getPageGraphData;
    }
    if (this._origDO) {
      Object.keys(this._origDO).forEach((name) => {
        documentOnlineService[name] = this._origDO[name];
      });
    }
  },
  methods: {
    // Monotonic local id for in-memory options (never collides with the
    // 9-char server ids).
    localUid() {
      this._uid = (this._uid || 0) + 1;
      return "loc_" + Date.now().toString(36) + "_" + this._uid;
    },
    // Drop hidden sections from a page's rows. In single-use mode there is no
    // student/school to identify, so we hide them. A SECTION boundary is a
    // NON_INTERACTABLE (kind 0) is_title item — these are the "I.- IDENTIFICACIÓN",
    // "- IDENTIFICACIÓN DE LOS PROFESIONALES…", "II.- FORTALEZAS…" titles.
    // (Interactive fields like the professional USER_SELECTORs are ALSO is_title
    // with the same style, but they are CONTENT, not boundaries, so they must
    // not stop the hiding.) When a boundary's text matches HIDDEN_SECTION_KEYS
    // (accent-insensitive substring) we hide it and every following row until
    // the next boundary at the same or a higher level (smaller style = higher).
    // Removing the rows (vs hiding) keeps them out of the render context too.
    filterHiddenSections(rows) {
      // The user opted to fill the identification page → keep those sections
      // (for paged docs this runs per-page; without it the kept first page
      // would be stripped of its rows).
      if (this.fillFirstPage) return rows;
      const HIDDEN_SECTION_KEYS = ["identificacion"];
      const norm = (s) =>
        (s || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .trim()
          .toLowerCase();
      const matches = (s) => {
        const t = norm(s);
        return !!t && HIDDEN_SECTION_KEYS.some((k) => t.indexOf(k) !== -1);
      };
      // Section boundary = a NON_INTERACTABLE (kind === 0) is_title field.
      // Interactive fields (USER_SELECTOR kind 4, etc.) are also is_title but
      // are CONTENT — they must NOT be treated as boundaries.
      const boundaryOf = (row) =>
        ((row && row.fields) || []).find(
          (f) => f && f.is_title && (f.kind === 0 || f.kind == null)
        ) || null;
      const out = [];
      let hiding = false;
      let sectionStyle = null;
      (rows || []).forEach((row) => {
        const boundary = boundaryOf(row);
        if (boundary && matches(boundary.text)) {
          // Start (or restart) a hidden section — hide its header row.
          hiding = true;
          sectionStyle = boundary.style;
          return;
        }
        if (
          hiding &&
          boundary &&
          boundary.style != null &&
          boundary.style <= sectionStyle
        ) {
          // Next section at the same or a higher level — stop hiding.
          hiding = false;
          sectionStyle = null;
        }
        if (!hiding) out.push(row);
      });
      return out;
    },
    // Populate the USER_SELECTOR options for studentless mode. There is no real
    // school user list to pick from, so each selector gets exactly one option —
    // the logged-in user — but ONLY the selector whose role matches the role the
    // user chose in the chat (`userSelect.role`); every other selector is left
    // empty. The matching one is also pre-selected (v-model = the user id) so
    // its data renders. Coordinator / Director RoleEnum variants are treated as
    // equivalent (the backend filters them together too).
    applyUserSelect(rows) {
      const us = this.userSelect;
      const EQUIV = [
        [0, 20], // Directora / Director
        [3, 30], // Coordinadora / Coordinador
      ];
      const roleMatches = (fieldRole, chosen) => {
        if (fieldRole == null || chosen == null) return false;
        const fr = Number(fieldRole);
        const ch = Number(chosen);
        if (fr === ch) return true;
        return EQUIV.some((g) => g.includes(fr) && g.includes(ch));
      };
      (rows || []).forEach((row) => {
        (row.fields || []).forEach((f) => {
          if (!f || f.kind !== 4) return;
          const match =
            us &&
            us.data &&
            us.data.id != null &&
            roleMatches(f.user_select_role, us.role);
          this.$set(f, "user_select_options", match ? [us.data] : []);
          if (match) {
            this.$set(this.fakeDocument.context, f.key, us.data.id);
          }
        });
      });
    },
    // Seed the local field_values with the empty shapes the modal's templates
    // expect for structured kinds — mirrors what DocumentOnline.get_context
    // produces for a real (empty) document. Without this, e.g. an OA field's
    // `field_values[key].oas` v-for reads `.oas` on undefined and Vue throws.
    // Only fills keys not already present, so it never clobbers an answer.
    initContext(rows) {
      const ctx = this.fakeDocument.context;
      (rows || []).forEach((row) => {
        (row.fields || []).forEach((f) => {
          if (!f || !f.key || ctx[f.key] !== undefined) return;
          let initial;
          switch (f.kind) {
            case 6: // OA_FIELD
            case 12: // OA_PROGRESSION
              initial = { oas: [], values: {} };
              break;
            case 7: // CUSTOM_CHECKBOX
              initial = [];
              break;
            case 4: // USER_SELECTOR — rendered as a select (like the real flow).
              // Its option is injected by applyUserSelect for the field whose
              // role matches the chosen user_role; the others stay empty. The
              // v-model holds the picked user id (or "").
              initial = "";
              break;
            case 3: // USER_CHECKBOX_ACHIEVEMENT
            case 8: // DOCUMENT_CHECKBOX_ACHIEVEMENT
            case 9: // OA_ACHIEVEMENT
              initial = {};
              break;
            default:
              return; // text / select / bool: v-model handles undefined
          }
          this.$set(ctx, f.key, initial);
        });
      });
    },
    emitPageState() {
      this.$emit("page-state", {
        isPaged: this.isPaged,
        current: this.currentPage,
        total: this.totalPages,
      });
    },
    // Drive the embedded DocumentOnline's pageIndex (b-tabs v-model). Moving to
    // a page lazy-loads its rows (getPageInfo) + seeds its context, so stepping
    // through every page guarantees the whole document is loaded before the
    // download on the last page.
    nextPage() {
      if (!this.$refs.doc) return;
      if (this.currentPage < this.totalPages - 1) {
        this.$refs.doc.pageIndex = this.currentPage + 1;
      }
    },
    prevPage() {
      if (!this.$refs.doc) return;
      if (this.currentPage > 0) {
        this.$refs.doc.pageIndex = this.currentPage - 1;
      }
    },
    // The live answers — same object the child mutates in place.
    getFieldValues() {
      return this.fakeDocument.context;
    },
  },
};
</script>

<style scoped>
.dynamic-document-form {
  width: 100%;
}
.ddf-loading {
  padding: 16px;
  color: #6b7280;
  font-style: italic;
}
/* Hide the b-tabs page headers: in the chatbot, navigation is driven only by
   the "Página anterior / siguiente" buttons so every page is visited (and
   lazy-loaded) in order before the download on the last page. */
.dynamic-document-form ::v-deep .nav-tabs {
  display: none !important;
}
</style>
